// src/components/layouts/MainLayout.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import { ThemeContext } from '../../context/ThemeContext';
import Navbar from '../../common/Navbar';
import Sidebar from '../../common/Sidebar';
import Footer from '../../common/Footer';
import GlobalSearchBar from '../common/GlobalSearchBar';
import RemotePrintSocketBridge from '../pos/RemotePrintSocketBridge';

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  z-index: 1;
  /* Transparent background to show gradient overlay */
  background-color: transparent;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
`;

// Swipe gesture handler - no button needed

const SidebarContainer = styled(motion.div)`
  flex-shrink: 0;
  transition: min-width ${props => props.theme.transitions.medium}, transform ${props => props.theme.transitions.medium};
  
  /* Desktop - hover behavior, always show icons when collapsed */
  @media (min-width: 1025px) {
    min-width: ${props => props.isHidden ? '0' : (props.isExpanded ? '250px' : '70px')};
    overflow: ${props => props.isHidden ? 'hidden' : 'visible'};
    position: sticky;
    top: 0;
    align-self: flex-start;
    height: 100vh;
    max-height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
  }
  
  /* Tablet - same behavior as mobile hamburger overlay */
  @media (min-width: 769px) and (max-width: 1024px) {
    position: fixed;
    top: 0px; /* Right after header (60px) */
    left: 0;
    bottom: 0;
    z-index: 100;
    min-width: ${props => props.isOpen ? '230px' : '70px'};
    transform: translateX(0);
    // box-shadow: 2px 0 20px rgba(0, 0, 0, 0.3);
  }
  
  /* Mobile - hamburger toggle, completely hidden */
  @media (max-width: 768px) {
    position: fixed;
    top: 0px; /* Right after header (60px) */
    left: 0;
    bottom: 0;
    z-index: 100;
    min-width: 250px; /* Full width when open */
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
    // box-shadow: 2px 0 20px rgba(0, 0, 0, 0.3);
  }
  
  /* Show as overlay when isPOSOverlay is true */
  ${props => props.isPOSOverlay && `
    position: fixed !important;
    top: 60px !important;
    left: 0 !important;
    bottom: 0 !important;
    z-index: 100 !important;
    min-width: 250px !important;
    transform: translateX(${props.isOpen ? '0' : '-100%'}) !important;
    transition: transform ${props.theme.transitions.medium} !important;
    box-shadow: ${props.isOpen ? '2px 0 20px rgba(0, 0, 0, 0.2)' : 'none'} !important;
  `}
`;

const ContentContainer = styled(motion.main)`
  flex: 1;
  padding: ${props => props.hideNavbar ? '0' : '60px 0 0 0'}; /* Only top padding for navbar, no side padding */
  transition: margin-left ${props => props.theme.transitions.medium};
  margin-left: ${props => props.sidebarOpen && props.isPOSRoute ? '250px' : '0'};
  overflow: hidden; /* Ensure content doesn't overflow */
  
  /* Responsive breakpoints */
  @media (max-width: 1024px) {
    /* Tablet */
    padding: ${props => props.hideNavbar ? '0' : '56px 0 0 70px'};
    margin-left: ${props => props.sidebarOpen && props.isPOSRoute ? '250px' : '0'};
  }
  
  @media (max-width: 768px) {
    /* Mobile — match fixed navbar height */
    padding: ${props => props.hideNavbar ? '0' : '52px 0 0 0'};
    margin-left: ${props => props.sidebarOpen && props.isPOSRoute ? '250px' : '0'};
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 60px; /* Start right after header */
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
  display: ${props => props.isOpen ? 'block' : 'none'};
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);

  @media (max-width: 768px) {
    top: 52px;
  }
`;

// Animation variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 0
  },
  in: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.15
    }
  },
  out: {
    opacity: 1,
    y: -20,
    transition: {
      duration: 0.15
    }
  }
};

// eslint-disable-next-line no-unused-vars
const SidebarToggleButton = styled(motion.button)`
  position: fixed;
  left: 1rem;
  top: 70px;
  z-index: 101;
  background: ${props => props.theme.name === 'light' 
    ? 'rgba(255, 255, 255, 0.9)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.theme.name === 'light' 
    ? 'rgba(0, 0, 0, 0.1)' 
    : 'rgba(255, 255, 255, 0.2)'};

  border-radius: ${props => props.theme.borderRadius.medium || '8px'};
  padding: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.theme.name === 'light'
    ? '0 4px 16px rgba(0, 0, 0, 0.1)'
    : '0 4px 16px rgba(0, 0, 0, 0.3)'
  };
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  color: ${props => props.theme.colors.text.primary};
  transition: all ${props => props.theme.transitions.short || '0.2s'};
  
  &:hover {
    transform: scale(1.1);
    box-shadow: ${props => props.theme.name === 'light'
      ? '0 6px 20px rgba(0, 0, 0, 0.15)'
      : '0 6px 20px rgba(0, 0, 0, 0.4)'
    };
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const MainLayout = () => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false); // Start collapsed on desktop
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { currentUser } = useContext(AuthContext);
  const { theme: themeContext } = useContext(ThemeContext);
  const location = useLocation();
  const sidebarRef = useRef(null);
  
  // Get the actual theme object
  const theme = themeContext || {};

  // Check if we're on POS route
  const isPOSRoute = location.pathname.startsWith('/pos');
  
  // Check if in fullscreen mode
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Detect mobile/tablet/desktop
  const [deviceType, setDeviceType] = useState('desktop'); // 'mobile', 'tablet', 'desktop'
  
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setDeviceType('mobile');
      } else if (width <= 1024) {
        setDeviceType('tablet');
      } else {
        setDeviceType('desktop');
      }
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => window.removeEventListener('resize', checkDevice);
  }, []);
  
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Hide sidebar on POS route or in fullscreen (dashboard keeps navbar + sidebar in Electron)
  const shouldHideSidebar = isPOSRoute || isFullscreen;
  
  // Calculate sidebar width based on device and state
  const getSidebarWidth = () => {
    if (shouldHideSidebar) return '0';
    if (deviceType === 'desktop') {
      return isSidebarExpanded ? '250px' : '70px';
    }
    // Tablet and mobile - sidebar is overlay, doesn't affect layout
    return '0';
  };
  
  const sidebarWidth = getSidebarWidth();
  
  // Don't hide navbar - always show it
  const shouldHideNavbar = isPOSRoute;

  const toggleSidebar = () => {
    // For mobile/tablet: toggle open/close via hamburger
    if (deviceType === 'mobile' || deviceType === 'tablet') {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    }
    // For desktop: this function won't be used (hover instead)
  };
  
  const hideSidebar = () => {
    setIsSidebarHidden(true);
  };
  
  // eslint-disable-next-line no-unused-vars
  const showSidebar = () => {
    setIsSidebarHidden(false);
    setIsSidebarExpanded(true);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen((previousState) => !previousState);
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  useEffect(() => {
    if (deviceType !== 'mobile' && deviceType !== 'tablet') return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = isMobileSidebarOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [deviceType, isMobileSidebarOpen]);
  
  // Handle sidebar hover on desktop
  const handleSidebarMouseEnter = () => {
    if (deviceType === 'desktop' && !shouldHideSidebar) {
      setIsSidebarExpanded(true);
    }
  };
  
  const handleSidebarMouseLeave = () => {
    if (deviceType === 'desktop' && !shouldHideSidebar) {
      setIsSidebarExpanded(false);
    }
  };
  
  // Handle click outside sidebar to close on desktop
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedMenuToggle = event.target.closest('[data-menu-toggle="true"]');
      if (clickedMenuToggle) {
        return;
      }

      if (deviceType === 'desktop' && 
          sidebarRef.current && 
          !sidebarRef.current.contains(event.target) && 
          isSidebarExpanded) {
        setIsSidebarExpanded(false);
      }
      
      // Close mobile/tablet sidebar when clicking outside
      if ((deviceType === 'mobile' || deviceType === 'tablet') && 
          sidebarRef.current && 
          !sidebarRef.current.contains(event.target) && 
          isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [deviceType, isSidebarExpanded, isMobileSidebarOpen]);

  // Swipe gesture detection for sidebar
  useEffect(() => {
    if (!shouldHideSidebar) return; // Only enable swipe when sidebar is hidden
    
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50; // Minimum distance for a swipe
    
    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
    };
    
    const handleTouchMove = (e) => {
      touchEndX = e.touches[0].clientX;
    };
    
    const handleTouchEnd = () => {
      const swipeDistance = touchEndX - touchStartX;
      
      // Swipe right from left edge to open sidebar
      if (swipeDistance > minSwipeDistance && touchStartX < 50) {
        setIsMobileSidebarOpen(true);
      }
      // Swipe left to close sidebar
      else if (swipeDistance < -minSwipeDistance && isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false);
      }
      
      touchStartX = 0;
      touchEndX = 0;
    };
    
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [shouldHideSidebar, isMobileSidebarOpen]);

  return (
    <LayoutContainer>
      {currentUser && <RemotePrintSocketBridge />}
      {/* Sidebar can be opened via swipe gesture or Navbar menu button */}
      {!isPOSRoute && (
      <Navbar 
        onMenuClick={toggleMobileSidebar} 
        user={currentUser}
        isSearchVisible={isSearchVisible}
        setIsSearchVisible={setIsSearchVisible}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSidebarExpanded={isSidebarExpanded}
        isMobileSidebarOpen={isMobileSidebarOpen}
      />
      )}
      {/* Global Search Bar - positioned between sidebar and navbar */}
      <GlobalSearchBar
        isVisible={isSearchVisible}
        onClose={() => setIsSearchVisible(false)}
        sidebarWidth={sidebarWidth}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
      
      <MainContent>
        {/* Render sidebar normally when not hidden, or as overlay when hidden */}
        {/* Hamburger menu is now only in the navbar header */}
        
        {!shouldHideSidebar ? (
          deviceType === 'desktop' ? (
            <SidebarContainer 
              ref={sidebarRef}
              isExpanded={isSidebarExpanded}
              isHidden={isSidebarHidden}
              isOpen={false}
              theme={theme}
              onMouseEnter={handleSidebarMouseEnter}
              onMouseLeave={handleSidebarMouseLeave}
            >
              <Sidebar 
                isExpanded={isSidebarExpanded} 
                toggleSidebar={toggleSidebar}
                hideSidebar={hideSidebar}
                userRole={currentUser?.role || 'guest'}
                deviceType={deviceType}
                showToggleButton={false}
                onNavItemClick={closeMobileSidebar}
              />
            </SidebarContainer>
          ) : (
            <>
              <SidebarContainer 
                ref={sidebarRef}
                isExpanded={deviceType === 'tablet' ? isMobileSidebarOpen : true}
                isOpen={isMobileSidebarOpen}
                theme={theme}
              >
                <Sidebar 
                  isExpanded={deviceType === 'tablet' ? isMobileSidebarOpen : true}
                  toggleSidebar={toggleSidebar}
                  userRole={currentUser?.role || 'guest'}
                  deviceType={deviceType}
                  showToggleButton={false}
                  onNavItemClick={closeMobileSidebar}
                />
              </SidebarContainer>
              <Overlay 
                isOpen={isMobileSidebarOpen}
                onClick={closeMobileSidebar}
                initial={{ opacity: 0 }}
                animate={{ opacity: isMobileSidebarOpen ? 1 : 0 }}
                transition={{ duration: 0.3 }}
              />
            </>
          )
        ) : (
          <>
            <SidebarContainer 
              ref={sidebarRef}
              isExpanded={true}
              isOpen={isMobileSidebarOpen}
              isPOSOverlay={true}
              theme={theme}
            >
              <Sidebar 
                isExpanded={true} 
                toggleSidebar={toggleSidebar}
                userRole={currentUser?.role || 'guest'}
                deviceType={deviceType}
                showToggleButton={false}
                onNavItemClick={closeMobileSidebar}
              />
            </SidebarContainer>
            <Overlay 
              isOpen={isMobileSidebarOpen}
              onClick={closeMobileSidebar}
              initial={{ opacity: 0 }}
              animate={{ opacity: isMobileSidebarOpen ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            />
          </>
        )}
        
        <ContentContainer 
          sidebarWidth={shouldHideSidebar ? '0' : (isSidebarExpanded ? '250px' : '70px')}
          sidebarOpen={isMobileSidebarOpen}
          isPOSRoute={isPOSRoute}
          hideNavbar={shouldHideNavbar}
          theme={theme}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              style={{ 
                minHeight: shouldHideNavbar ? '100vh' : 'calc(100vh - 60px)',
                width: '100%',
                height: '100%'
              }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
          
          <Footer />
        </ContentContainer>
      </MainContent>
    </LayoutContainer>
  );
};

export default MainLayout;